import { useMediaQuery } from '@/utils/useMediaQuery';
import { f7 } from 'framework7-react';
import { useEffect, useRef, type CSSProperties, type HTMLInputTypeAttribute } from 'react';

/**
 * Compact pill-shaped date/time input.
 *
 * `type="date"` overlays a transparent native `<input type="date">` over the
 * pill: a native date input cannot be CSS-shrunk to its value before Chrome 123
 * (`field-sizing`), so the value is rendered as plain text (which sizes
 * naturally) and the native input only captures the tap. The pill hugs the
 * value in every browser.
 *
 * `type="time"` is chosen by VIEWPORT width (768px, the app's desktop
 * breakpoint), not platform, so the behaviour is predictable and switches live
 * when the window is resized:
 *   - narrow (phone / narrow window) → Framework7's 24-hour drum/wheel `Picker`
 *     (popover anchored to the pill; a hidden input is the anchor F7 needs;
 *     hours/minutes columns; the mouse wheel is intercepted to advance one stop
 *     per notch instead of the native ~3-row jump).
 *   - wide (desktop) → a plain, typeable native `<input type="time">` forced to
 *     24h via `lang="en-GB"`.
 *
 * Usage:
 *   <ListItem noChevron title="Start">
 *     <ChipInput slot="after" type="time" value={t} onChange={setT} />
 *   </ListItem>
 */

const chipStyle: CSSProperties = {
  position: 'relative',
  background: 'rgba(120,120,128,0.12)',
  borderRadius: 6,
  padding: '4px 10px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

const valueStyle: CSSProperties = {
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  whiteSpace: 'nowrap',
};

const overlayInputStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'transparent',
  opacity: 0,
  cursor: 'pointer',
};

/** Minutes between wheel stops. 1-minute granularity everywhere, matching the
 *  order-hours picker - callers can still pass a coarser `step` explicitly. */
const DEFAULT_MINUTE_STEP = 1;
/** Matches --f7-picker-item-height; fallback only - we measure the live row. */
const PICKER_ITEM_HEIGHT = 36;

const pad = (n: number) => String(n).padStart(2, '0');

/** Visible pill-styled native time field for desktop — a real, typeable
 *  <input type="time"> (HH:MM spinner), not the wheel or the overlay. */
const timeFieldStyle: CSSProperties = {
  background: 'rgba(120,120,128,0.12)',
  border: 'none',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  fontFamily: 'inherit',
};

/** Locale-native AM/PM strings (e.g. "AM"/"PM", "vorm."/"nachm."). */
function localizedPeriods(): [string, string] {
  try {
    const fmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', hour12: true });
    const period = (h: number) =>
      fmt.formatToParts(new Date(2000, 0, 1, h)).find((p) => p.type === 'dayPeriod')?.value;
    return [period(1) ?? 'AM', period(13) ?? 'PM'];
  } catch {
    return ['AM', 'PM'];
  }
}

function formatValue(type: HTMLInputTypeAttribute, value: string): string {
  if (!value) return '';
  if (type === 'date') {
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return value;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  if (type === 'time') {
    const [h, min] = value.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(min)) return value;
    // Always 24h (no AM/PM) regardless of browser locale.
    return new Date(2000, 0, 1, h, min).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return value;
}

interface ChipInputProps {
  type: HTMLInputTypeAttribute;
  value: string;
  onChange: (value: string) => void;
  /** F7 slot name (e.g. "after") - forwarded onto the wrapping <label>. */
  slot?: string;
  min?: string;
  max?: string;
  /** For `type="time"`: minutes between wheel stops (default 5). */
  step?: string | number;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Time variant: a pill that opens Framework7's drum/wheel picker as a popover
 * anchored to the pill. Cross-browser consistent (no native time input).
 */
function TimeChip({
  value,
  onChange,
  slot,
  step,
  disabled,
  'aria-label': ariaLabel,
}: Omit<ChipInputProps, 'type' | 'min' | 'max'>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<ReturnType<typeof f7.picker.create> | null>(null);
  // Refs so the persistent picker always reaches the latest props without
  // being recreated on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  // HH:mm → column value array, set once the picker exists (locale-dependent).
  const toArrRef = useRef<((hhmm: string) => string[]) | null>(null);

  const minuteStep = Number(step) > 0 ? Number(step) : DEFAULT_MINUTE_STEP;
  // Force 24h wheel (hours 00–23, no AM/PM column) regardless of locale.
  const hour12 = false;

  // Build the picker once. Recreated only if the locale clock style or the
  // minute step changes - never on value changes (those go through setValue).
  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;

    const [amText, pmText] = localizedPeriods();
    const minutes: string[] = [];
    for (let m = 0; m < 60; m += minuteStep) minutes.push(pad(m));
    // Snap an off-step stored minute (e.g. "17:03") to the nearest wheel stop.
    const snap = (M: number) =>
      minutes.reduce(
        (best, cur) => (Math.abs(Number(cur) - M) < Math.abs(Number(best) - M) ? cur : best),
        minutes[0],
      );

    const toArr = (hhmm: string): string[] => {
      const [H = 0, M = 0] = hhmm.split(':').map(Number);
      if (hour12) {
        const h12 = H % 12 === 0 ? 12 : H % 12;
        return [String(h12), snap(M), H >= 12 ? pmText : amText];
      }
      return [pad(H), snap(M)];
    };
    const toHHmm = (vals: string[]): string => {
      if (hour12) {
        let h = Number(vals[0]) % 12;
        if (vals[2] === pmText) h += 12;
        return `${pad(h)}:${vals[1]}`;
      }
      return `${vals[0]}:${vals[1]}`;
    };
    toArrRef.current = toArr;

    // Divider columns carry no value, so they're excluded from the value array.
    const cols = hour12
      ? [
          { values: Array.from({ length: 12 }, (_, i) => String(i + 1)), textAlign: 'right' },
          { divider: true, content: ':' },
          { values: minutes },
          { values: [amText, pmText] },
        ]
      : [
          { values: Array.from({ length: 24 }, (_, i) => pad(i)), textAlign: 'right' },
          { divider: true, content: ':' },
          { values: minutes },
        ];

    // Mouse wheel: the column is a native scroll container with scroll-snap, so
    // one notch scrolls ~3 rows. Override it to move exactly one stop per notch.
    let wheelTargets: HTMLElement[] = [];
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      const row = el.querySelector('.picker-item') as HTMLElement | null;
      const h = row?.offsetHeight || PICKER_ITEM_HEIGHT;
      el.scrollBy({ top: (e.deltaY > 0 ? 1 : -1) * h, behavior: 'smooth' });
    };
    const attachWheel = (p: { $el?: { 0?: HTMLElement } }) => {
      const root = p.$el?.[0];
      if (!root) return;
      wheelTargets = Array.from(root.querySelectorAll<HTMLElement>('.picker-items'));
      wheelTargets.forEach((el) => el.addEventListener('wheel', onWheel, { passive: false }));
    };
    const detachWheel = () => {
      wheelTargets.forEach((el) => el.removeEventListener('wheel', onWheel));
      wheelTargets = [];
    };

    const picker = f7.picker.create({
      inputEl,
      // Popover anchored to the pill - same wheel on Mac, non-Mac, desktop,
      // and mobile (no native time input, no bottom sheet).
      openIn: 'popover',
      rotateEffect: true,
      inputReadOnly: true,
      toolbar: false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- F7 column union
      cols: cols as any,
      value: toArr(valueRef.current),
      on: {
        change(_p: unknown, vals: unknown) {
          onChangeRef.current(toHHmm(vals as string[]));
        },
        opened: attachWheel,
        close: detachWheel,
      },
    });
    pickerRef.current = picker;

    return () => {
      detachWheel();
      picker.destroy();
      pickerRef.current = null;
      toArrRef.current = null;
    };
  }, [hour12, minuteStep]);

  // Keep the wheel in sync when the value is changed from outside, but skip when
  // it already matches (avoids fighting an in-progress drag/scroll).
  useEffect(() => {
    const picker = pickerRef.current;
    const toArr = toArrRef.current;
    if (!picker || !toArr) return;
    const target = toArr(value);
    const current = picker.value as string[] | undefined;
    if (!current || current.join('|') !== target.join('|')) {
      picker.setValue(target);
    }
  }, [value]);

  return (
    <label
      slot={slot}
      style={disabled ? { ...chipStyle, cursor: 'default', opacity: 0.5 } : chipStyle}
    >
      <span style={valueStyle}>{formatValue('time', value)}</span>
      <input
        ref={inputRef}
        type="text"
        readOnly
        disabled={disabled}
        aria-label={ariaLabel}
        style={overlayInputStyle}
      />
    </label>
  );
}

export function ChipInput({
  type,
  value,
  onChange,
  slot,
  min,
  max,
  step,
  disabled,
  'aria-label': ariaLabel,
}: ChipInputProps) {
  // Decide by VIEWPORT width (matches the app's 768px desktop breakpoint), so the
  // mobile scroll returns whenever the browser window is phone-sized — on a real
  // phone OR a narrowed desktop window. Touch hardware alone wouldn't flip in
  // responsive testing, and it's the same 24-hour wheel on every platform
  // (including iOS) so the format is consistent.
  const isWide = useMediaQuery('(min-width: 768px)');

  // Time UX:
  //   - narrow (phone / narrow window) → Framework7 drum/wheel picker, 24h
  //   - wide   (desktop)               → plain, typeable native <input type="time">
  if (type === 'time' && !isWide) {
    return (
      <TimeChip
        value={value}
        onChange={onChange}
        slot={slot}
        step={step}
        disabled={disabled}
        aria-label={ariaLabel}
      />
    );
  }
  if (type === 'time' && isWide) {
    return (
      <input
        slot={slot}
        type="time"
        // Force a 24-hour field (no AM/PM), matching the wheel picker. The native
        // time input's clock style follows the element's `lang`; en-GB is 24h.
        lang="en-GB"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        disabled={disabled}
        aria-label={ariaLabel}
        style={disabled ? { ...timeFieldStyle, opacity: 0.5 } : timeFieldStyle}
      />
    );
  }

  return (
    <label
      slot={slot}
      style={disabled ? { ...chipStyle, cursor: 'default', opacity: 0.5 } : chipStyle}
    >
      <span style={valueStyle}>{formatValue(type, value)}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => {
          const el = e.currentTarget;
          if (typeof el.showPicker === 'function') {
            try {
              el.showPicker();
            } catch {
              // showPicker throws without a user gesture / when unsupported;
              // the field still focuses and the picker opens on tap anyway.
            }
          }
        }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        aria-label={ariaLabel}
        style={overlayInputStyle}
      />
    </label>
  );
}
